const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

export async function generateHeartbeat(data) {

	// --- costs caching logic ---
	let dataCosts;
	const cached = JSON.parse(localStorage.getItem("costsCache") || "null");
	const presentMoment = Date.now();

	if (cached && presentMoment - cached.timestamp < 24 * 60 * 60 * 1000) {
		// Use cached data
		console.log("Using local storage cache");
		dataCosts = cached;
	} else {
		// Fetch fresh data
		const resCosts = await fetch(`${apiBaseUrl}/api/costs-net`);
		const freshData = await resCosts.json();
		dataCosts = freshData;
	}

	const costsFormattedByProduct = (dataCosts.byProductData || [])
		.map(item => ({
			product: item.product,
			cost: item.cost
		}))
		.sort((a, b) => b.cost - a.cost);

	// --- PDF setup ---
	const jsPDFModule = await import("jspdf");
	const autoTableModule = await import("jspdf-autotable");

	const jsPDF = jsPDFModule.default;
	const autoTable = autoTableModule.default;

	const pdf = new jsPDF({ orientation: "landscape" });
	const pageWidth = pdf.internal.pageSize.getWidth();
	let totalPages = 1; // placeholder, real value fixed later

	// --- Header function (kept unchanged) ---
	const addHeader = (title) => {
		const currentPage = pdf.getNumberOfPages();
		pdf.setFontSize(22);
		const titleWidth = pdf.getTextWidth(title);
		pdf.text(title, (pageWidth - titleWidth) / 2, 15);

	};

	// --- Main header ---
	pdf.setFontSize(22);
	const title = "System Heartbeat Report";
	const titleWidth = pdf.getTextWidth(title);
	pdf.text(title, (pageWidth - titleWidth) / 2, 15);

	// --- Subheader ---
	pdf.setFontSize(12);
	const now = new Date();
	const formattedDate = now.toLocaleString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit"
	});
	const subheader = `Generated: ${formattedDate}`;
	const subheaderWidth = pdf.getTextWidth(subheader);
	pdf.text(subheader, (pageWidth - subheaderWidth) / 2, 25);

	// --- Sections ---
	const sections = [
		{ name: "Tasks", rows: data.tasks },
		{ name: "Purchase Orders", rows: data.purchaseOrders },
		{ name: "Quotations", rows: data.quotations },
		{ name: "Inventory", rows: data.inventory },
		{ name: "Invoices", rows: data.invoices },
		{ name: "Costs", rows: data.costs },
		{ name: "Customers", rows: data.customers },
		{ name: "Suppliers", rows: data.suppliers },
		{
			name: `System Costs (USD, ${now.toLocaleString("en-US", { month: "long" })})`,
			rows: costsFormattedByProduct
		}
	];

	// --- Formatters (unchanged) ---
	const formatters = {
		Tasks: (rows) =>
			rows.map(t => ({
				From: t.fromName,
				To: t.toName,
				Task: t.task,
				CreatedAt: t.date
					? new Date(t.date).toLocaleString("en-US", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit"
					})
					: t.date,
				Importance: t.importance
			})),

		"Purchase Orders": (rows) =>
			rows.map(po => ({
				ID: po.Id,
				Supplier: po.supplier_name,
				POItems: po.PurchaseOrderItems,
				Total: po.total,
				ReferenceTag: po.item_name,
				PaymentTerm: po.payment_term,
				Region: po.region,
				Availability: po.availability,
				Status: po.status,
				CreatedAt: po.created_at
					? new Date(po.created_at).toLocaleString("en-US", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit"
					})
					: po.created_at
			})),

		"Quotations": (rows) =>
			rows.map(po => ({
				ID: po.id,
				Customer: po.customerName,
				QuotationItems: po.QuotationItems,
				Total: po.total,
				ReferenceTag: po.item,
				PaymentTerm: po.paymentTerm,
				Region: po.region,
				Availability: po.availability,
				Status: po.status,
				CreatedAt: po.CreatedAt
					? new Date(po.date).toLocaleString("en-US", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit"
					})
					: po.date
			})),

		Inventory: (rows) => {
			const grouped = {};
			rows.forEach(item => {
				const w = item.warehouse || "Default Warehouse";
				if (!grouped[w]) grouped[w] = [];
				grouped[w].push(item);
			});

			return Object.entries(grouped).map(([warehouse, items]) => ({
				Warehouse: warehouse,
				Items: items.map(i => ({
					ID: i.id,
					"Goods Name": i.name,
					Type: i.type,
					Price: i.price,
					Quantity: i.quantity,
					NetWorth: i.netWorth,
					CreatedAt: i.CreatedAt
						? new Date(i.CreatedAt).toLocaleString("en-US", {
							year: "numeric",
							month: "long",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit"
						})
						: i.CreatedAt
				}))
			}));
		},

		"Invoices": (rows) =>
			rows.map(po => ({
				ID: po.id,
				Customer: po.customerName,
				InvoiceItems: po.InvoiceItems,
				Total: po.total,
				ReferenceTag: po.itemName,
				Warehouse: po.warehouse,
				PaymentTerm: po.status,
				Region: po.region,
				CreatedAt: po.createdAt
					? new Date(po.createdAt).toLocaleString("en-US", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit"
					})
					: po.createdAt
			})),

		"Costs": (rows) =>
			rows.map(po => ({
				ID: po.id,
				"Cost Incurrer": po.userName,
				Category: po.category,
				Amount: po.amount,
				Description: po.description,
				Advance: po.advance,
				"PO-ID (If Purchase Order)": po.poId,
				CreatedAt: po.date
					? new Date(po.date).toLocaleString("en-US", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit"
					})
					: po.date
			})),

		"Customers": (rows) =>
			rows.map(po => ({
				ID: po.Id,
				Name: po.Name,
				Revenue: po.Revenue,
				Region: po.Region,
				Month: po.month,
				Type: po.Type,
				CreatedAt: po.CreatedAt
					? new Date(po.CreatedAt).toLocaleString("en-US", {
						year: "numeric",
						month: "long",
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit"
					})
					: po.CreatedAt
			})),

		"Suppliers": (rows) =>
			rows.map(s => {
				const { DeletedAt, ...rest } = s;
				return {
					...rest,
					CreatedAt: s.CreatedAt
						? new Date(s.CreatedAt).toLocaleDateString("en-US", {
							year: "numeric",
							month: "long",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit"
						})
						: s.CreatedAt
				};
			})
	};


    const sectionPages = {};

	// --- Render all sections (unchanged logic) ---
	sections.forEach((section) => {
		const formattedRows = formatters[section.name]?.(section.rows) || section.rows;
        sectionPages[section.name] = pdf.getNumberOfPages() + 1;

		if (section.name === "Inventory") {
			pdf.addPage();
			addHeader(section.name);

			let startY = 30;

			formattedRows.forEach(wh => {
				pdf.setFontSize(15);
				pdf.text(`Warehouse: ${wh.Warehouse}`, 10, startY);
				startY += 8;

				autoTable(pdf, {
					startY: startY,
					head: [['ID', 'Goods Name', 'Type', 'Price', 'Quantity', 'NetWorth', 'CreatedAt']],
					body: wh.Items.map(i => [
						i.ID,
						i['Goods Name'],
						i.Type,
						i.Price,
						i.Quantity,
						i.NetWorth,
						i.CreatedAt
					]),
					styles: { fontSize: 10, cellPadding: 2 },
					margin: { left: 15, right: 15 },
					didDrawPage: (data) => {
						startY = data.cursor.y + 5;
					}
				});
			});

		} else {
			pdf.addPage();
			addHeader(section.name);

			autoTable(pdf, {
				startY: 25,
				head: [Object.keys(formattedRows?.[0] || {})],
				body: formattedRows?.map(r => Object.values(r)) || []
			});
		}
	});
	
	console.log(sectionPages);

	// --- Index page ---
 
 	totalPages = pdf.getNumberOfPages();	// REAL total pages now

    pdf.setPage(1);                 // go back to index page
    pdf.setFontSize(18);
    pdf.text("Index (Click on item to jump to section)", (pageWidth - subheaderWidth) / 2, 45);
    pdf.setFontSize(12);
    
    let yPos = 55;    
    // draw links with correct pages
    sections.forEach((section, idx) => {
        pdf.textWithLink(
            `${idx + 1}. ${section.name}`,
            (pageWidth - subheaderWidth) / 2,
            yPos,
            { pageNumber: sectionPages[section.name] }
        );
        yPos += 10;
    });
	// ---------------------------------------------------------
	//		FIX PAGE NUMBERS (SECOND PASS)
	// ---------------------------------------------------------


	for (let p = 1; p <= totalPages; p++) {
		pdf.setPage(p);

		const pageNumText = `Page ${p} of ${totalPages}`;
		pdf.setFontSize(12);
		const width = pdf.getTextWidth(pageNumText);

		// Draw correct page number at top-right
		pdf.text(pageNumText, pageWidth - width - 40, 15);
	}

	// --- Save file ---
	const timestamp =
		now.toLocaleDateString("en-GB").replace(/\//g, "_") +
		"_" +
		now.toLocaleTimeString("en-GB").replace(/[: ]/g, "_");

	pdf.save(`heartbeat_${timestamp}.pdf`);
}
